import type { AnalyticsRecord } from '@/utils/services/analytics/AnalyticsDataService';

import type { KPIDataType, KpiType } from '@/lib/analytics/kpis/kpi.type';
import { FACEBOOK } from '@/lib/socialProfile';

import { KPIDefault } from '@/components/base/Kpi/KpiDefault';

/**
 * Split values for organic/paid breakdown
 */
export type SplitValues = {
    organic: number;
    paid: number;
    hasOrganicViralData?: boolean;
} | null;

type GetSplitValuesParams = {
    data: KPIDataType | null | undefined;
    config: KpiType<typeof KPIDefault>;
    socialNetworkType: string;
};

class KpiService {
    /**
     * Extract KPI data from analytics response and enrich with split values.
     *
     * @param analytics - The analytics response object
     * @param config - The KPI config containing id and optional splits mapping
     * @returns The enriched KPI data with split values, or empty object if no data
     */
    extractKpiDataWithSplits = (
        analytics: AnalyticsRecord | null | undefined,
        config: KpiType<typeof KPIDefault>
    ): KPIDataType => {
        if (!analytics) {
            return {} as KPIDataType;
        }

        const baseData = { ...analytics[config.id] } as KPIDataType;

        if (!baseData || !config.splits) {
            return baseData || ({} as KPIDataType);
        }

        Object.keys(config.splits).forEach((split) => {
            const key = config.splits![split];
            baseData[key] = analytics[key]?.value ?? 0;
        });

        return baseData;
    };

    /**
     * Get split values (organic/paid) from KPI data.
     * Supports both V2 (config.splits) and V1 (data.split) formats.
     *
     * @param params.data - The KPI data object
     * @param params.config - The KPI config containing optional splits mapping
     * @param params.socialNetworkType - The social network type (needed for Facebook-specific logic)
     * @returns Split values object or null if no split data available
     */
    getSplitValues = ({ data, config, socialNetworkType }: GetSplitValuesParams): SplitValues => {
        if (!data) return null;

        // V2: New system with config.splits (Twitter X API v2)
        if (config.splits) {
            const organic = data[config.splits.organic] as number | undefined;
            const paid = data[config.splits.paid] as number | undefined;

            if (organic !== undefined && paid !== undefined) {
                return { organic, paid };
            }
        }

        // V1: Legacy system with data.split (backwards compatibility)
        if (data.split) {
            const organicValue =
                socialNetworkType === FACEBOOK
                    ? data.split.organic_viral ?? data.split.organic
                    : data.split.organic;

            return {
                organic: organicValue,
                paid: data.split.paid,
                hasOrganicViralData: !!data.split.organic_viral_data
            };
        }

        return null;
    };
}

const kpiService = new KpiService();

export { kpiService as KpiService };
